import type { Stripe, StripeElements } from '@stripe/stripe-js'
import type { ComputedRef, ShallowRef } from 'vue'

import type {
  ConfirmPaymentOptions,
  CreatePaymentMethodOptions,
  StripeElementsStatus,
  TixConfirmPaymentResult,
  TixPaymentMethodResult,
  TixSubmitResult,
} from './types'

interface StripeActionsDeps {
  stripe: ShallowRef<Stripe | null>
  elements: ShallowRef<StripeElements | null>
  status: ComputedRef<StripeElementsStatus>
}

/**
 * Thin, pre-bound wrappers over the live `stripe`/`elements` handles. They
 * inject the composable's own `elements` instance so callers never touch it,
 * but own no sequencing, API calls, captcha, or billing data.
 */
export function createStripeActions({ stripe, elements, status }: StripeActionsDeps) {
  /**
   * Validate the Payment Element and collect wallet data. Run in parallel with captcha (if required).
   */
  async function submit(): Promise<TixSubmitResult> {
    if (!stripe.value || !elements.value) {
      return {
        error: new Error(`submit() failed — Stripe is not ready (status: '${status.value}').`),
      }
    }

    const result = await elements.value.submit()

    if (result.error) return { stripeError: result.error }
    return { selectedPaymentMethod: result.selectedPaymentMethod }
  }

  /**
   * Turn the collected details into a PaymentMethod whose `id` is posted to tix-api.
   */
  async function createPaymentMethod(createOptions?: CreatePaymentMethodOptions): Promise<TixPaymentMethodResult> {
    if (!stripe.value || !elements.value) {
      return {
        error: new Error(`createPaymentMethod() failed — Stripe is not ready (status: '${status.value}').`),
      }
    }

    const result = await stripe.value.createPaymentMethod({ elements: elements.value, ...createOptions })

    if (result.error) return { stripeError: result.error }
    return result
  }

  /**
   * Confirm the PaymentIntent that was created against the `createPaymentMethod` result.
   *
   * Unlike the other two actions this one does *not* inject the `Elements` instance — Stripe treats
   * `elements` and `confirmParams.payment_method` as mutually exclusive, and passing `elements` makes
   * it re-collect payment details from the Payment Element. That is always wrong here: wallet
   * payments come from the Express Checkout Element and leave the Payment Element empty, and the
   * intent already has the caller's PaymentMethod attached.
   */
  async function confirmPayment({
    clientSecret,
    confirmParams,
  }: ConfirmPaymentOptions): Promise<TixConfirmPaymentResult> {
    if (!stripe.value || !elements.value) {
      return {
        error: new Error(`confirmPayment() failed — Stripe is not ready (status: '${status.value}').`),
      }
    }

    const result = await stripe.value.confirmPayment({
      clientSecret,
      confirmParams,
      redirect: 'if_required',
    })

    if (result.error) return { stripeError: result.error }
    return result
  }

  return { submit, createPaymentMethod, confirmPayment }
}
